/**
 * Forgot Password Page
 *
 * Implements the password reset request flow using Supabase Auth.
 * Users enter their email to receive a password reset link.
 *
 * Features:
 * - Email validation with react-hook-form and Zod
 * - Real-time error feedback
 * - Confirmation message after successful request
 * - Link back to login
 *
 * See: docs/v2/03-auth-and-security.md
 *
 * ## E2E test additions (GitHub issue #494 — "E2E: Authentication —
 * login, signup, OTP, password reset, session management")
 *
 * Added a `sonner` `toast.success`/`toast.error` alongside the existing
 * inline confirmation panel / `apiError` banner, and a home-linking
 * "logo" heading (same rationale as `Login.tsx`'s identical change).
 *
 * NOTE on the ticket's "Non-existent email -> error" bullet: Supabase's
 * `resetPasswordForEmail()` deliberately always resolves successfully
 * regardless of whether the email is registered (the exact same
 * anti-user-enumeration rationale documented in `Login.tsx`'s
 * `toFriendlyLoginError()` doc-comment — a password-reset endpoint that
 * says "no account found" lets an attacker enumerate registered emails).
 * So a non-existent email here shows the same "Check Your Email"
 * confirmation as a real one; there is no real-backend error response to
 * distinguish it from. `e2e/password-reset-auth-flow.spec.ts` covers the
 * "error" case by mocking a genuine transport/5xx failure from Supabase's
 * `/auth/v1/recover` endpoint instead.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { forgotPasswordSchema, type ForgotPasswordFormData } from '@/lib/validation/auth';
import { requestPasswordReset } from '@/lib/auth/supabaseAuth';

export default function ForgotPasswordPage() {
  const [apiError, setApiError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [sentEmail, setSentEmail] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsSubmitting(true);
    setApiError(null);

    const { success, error } = await requestPasswordReset(data.email);

    if (error || !success) {
      const message = error?.message || 'Failed to send password reset email';
      setApiError(message);
      toast.error(message);
      setIsSubmitting(false);
      return;
    }

    // Show confirmation message
    setSentEmail(data.email);
    setEmailSent(true);
    toast.success(`Password reset link sent to ${data.email}`);
  };

  if (emailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-full max-w-md p-8 space-y-6 bg-card border border-border rounded-lg shadow-lg">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-green-600">
              Check Your Email
            </h1>
            <p className="text-muted-foreground mt-4">
              We've sent a password reset link to <strong>{sentEmail}</strong>.
            </p>
            <p className="text-sm text-muted-foreground mt-4">
              Click the link in your email to reset your password. The link expires in 1 hour.
            </p>
            <button
              onClick={() => {
                setEmailSent(false);
                setSentEmail(null);
              }}
              className="mt-6 text-primary hover:underline font-medium"
            >
              Send another email
            </button>
            <div className="mt-4 pt-4 border-t border-border">
              <Link
                to="/login"
                className="text-primary hover:underline font-medium text-sm"
              >
                Back to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-full max-w-md p-8 space-y-6 bg-card border border-border rounded-lg shadow-lg">
        <div className="text-center">
          <Link to="/" data-testid="forgot-password-logo">
            <h1 className="text-3xl font-bold">Hero Hire AI</h1>
          </Link>
          <p className="text-muted-foreground mt-2">Reset your password</p>
        </div>

        {apiError && (
          <div className="p-4 bg-destructive/10 border border-destructive/30 text-destructive rounded-md text-sm">
            {apiError}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium mb-2">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              placeholder="you@example.com"
              {...register('email')}
              className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-destructive">
                {errors.email.message}
              </p>
            )}
          </div>

          <p className="text-sm text-muted-foreground">
            Enter the email address associated with your account, and we'll send you a link to reset your password.
          </p>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full px-4 py-2 bg-primary text-primary-foreground font-medium rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? 'Sending reset link...' : 'Send Reset Link'}
          </button>
        </form>

        {/* Links to other pages */}
        <div className="space-y-2 text-center text-sm text-muted-foreground">
          <div>
            Remember your password?{' '}
            <Link to="/login" className="text-primary hover:underline font-medium">
              Sign In
            </Link>
          </div>
          <div>
            Don't have an account?{' '}
            <Link to="/signup" className="text-primary hover:underline font-medium">
              Create one
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
